// @flow
import * as React from 'react';
import Button from '@material-ui/core/Button';
import { FormattedMessage, useIntl } from 'react-intl';

import Form, { type Props as FormProps } from '~plugins/form/components/Form';
import withSnackbarFeedback from '~plugins/form/hocs/withSnackbarFeedback';
import TextField from '~plugins/form/components/TextField';
import CheckboxWithLabel from '~plugins/form/components/CheckboxWithLabel';
import RichText from '~plugins/prismic/components/RichText';
import hasRichTextFieldValue from '~plugins/prismic/helpers/hasRichTextFieldValue';
import type { ContactFormValues } from './types';
import messages from './messages';
import type { PrismicContactForm } from '~schema';

const EnhancedForm = withSnackbarFeedback<
  ContactFormValues,
  FormProps<ContactFormValues>,
>()(Form);

export type Props = {
  ...$Exact<FormProps<ContactFormValues>>,
  node: ?PrismicContactForm,
};

const ContactForm = ({ initialValues, node, ...props }: Props) => {
  const intl = useIntl();

  return (
    <EnhancedForm
      {...props}
      node={node}
      messages={messages}
      initialValues={React.useMemo(
        () => ({
          email: '',
          subject: '',
          body: '',
          ...initialValues,
        }),
        [initialValues],
      )}
    >
      <div>
        <TextField
          type="text"
          name="name"
          label={
            node?.data?.label_name?.text || (
              <FormattedMessage {...messages.labels.name} />
            )
          }
          fullWidth
        />
        <TextField
          type="email"
          name="email"
          label={
            node?.data?.label_email?.text || (
              <FormattedMessage {...messages.labels.email} />
            )
          }
          fullWidth
        />
        <TextField
          type="text"
          name="subject"
          label={
            node?.data?.label_subject?.text || (
              <FormattedMessage {...messages.labels.subject} />
            )
          }
          fullWidth
        />
        <TextField
          type="text"
          name="body"
          label={
            node?.data?.label_body?.text || (
              <FormattedMessage {...messages.labels.body} />
            )
          }
          fullWidth
          multiline
          rows={5}
        />
        {hasRichTextFieldValue(node?.data?.label_accept_privacy_policy) ? (
          <CheckboxWithLabel
            name="acceptPrivacyPolicy"
            required
            label={<RichText {...node?.data?.label_accept_privacy_policy} />}
            aria-label={
              node?.data?.label_accept_privacy_policy?.text ||
              intl.formatMessage(messages.labels.privacyPolicy)
            }
          />
        ) : null}
      </div>
      <div>
        <Button type="submit" variant="contained">
          {node?.data?.label_submit?.text || (
            <FormattedMessage {...messages.labels.submit} />
          )}
        </Button>
      </div>
    </EnhancedForm>
  );
};

export default ContactForm;
